import { antdTheme, teamNepal } from "@project/shared"
import "animate.css/animate.min.css"
import { ConfigProvider, theme as antTheme } from "antd"
import { AppProps } from "next/app"
import { useRouter } from "next/router"
import { QueryClient, QueryClientProvider } from "react-query"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"
import styled, { ThemeProvider } from "styled-components"
import { HeaderComponent } from "../components"
import { useLenis } from "../components/hooks/useLenis"
import { GlobalStyles } from "../utils"
import React, { useEffect, useState } from "react"
import useDeviceTheme from "@project/shared/src/useDeviceTheme"
import "lenis/dist/lenis.css"
import { MailOutlined } from "@ant-design/icons"

const queryClient = new QueryClient({ defaultOptions: {} })

const MainWrapper = styled.div<any>`
  ${({ isContentLoaded }) =>
    isContentLoaded &&
    `height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      .animating-logo {
        @media (max-width: 650px) {
          width: 100%;
        }
      }
    `};
`

const FloatingContactButton = styled.button`
  position: fixed;
  right: 0;
  top: 70vh;
  z-index: 1000;
  padding: 16px;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px 0 0 25px;
  background: linear-gradient(
    87.75deg,
    #0058ee 0%,
    rgba(54, 215, 183, 0.8) 100%
  );
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, opacity 0.2s ease;
  &:hover {
    opacity: 0.95;
  }
  .icon {
    svg {
      width: 32px;
      height: 32px;
    }
    font-size: 32px !important;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 1023px) {
    padding: 8px 16px;
    top: 72vh;

    .icon {
      svg {
        width: 24px;
        height: 24px;
      }
    }
  }
`
const scrollToSectionWithOffset = async (
  router,
  route: string,
  elementId: string
) => {
  await router.push(route)

  requestAnimationFrame(() => {
    const element = document.getElementById(elementId)

    if (!element) return

    let offset = 80

    if (window.innerWidth <= 600) {
      offset = 40
    } else if (window.innerWidth <= 1023) {
      offset = 50
    }

    const top = element.getBoundingClientRect().top + window.scrollY - offset

    window.scrollTo({
      top,
      behavior: "smooth",
    })
  })
}

const MyApp = ({ Component, pageProps }: AppProps) => {
  const [isDOMLoaded, setIsDOMLoaded] = useState(false)
  const isDarkTheme = useDeviceTheme()
  const router = useRouter()

  useLenis({ enabled: isDOMLoaded })

  useEffect(() => {
    const handleLoad = () => {
      setIsDOMLoaded(true)
    }
    if (document.readyState === "complete") {
      setIsDOMLoaded(true)
    } else {
      window.addEventListener("load", handleLoad)
    }
    return () => {
      window.removeEventListener("load", handleLoad)
    }
  }, [])

  return (
    <MainWrapper isContentLoaded={!isDOMLoaded}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          theme={isDarkTheme ? teamNepal.darkTheme : teamNepal.lightTheme}
        >
          <ConfigProvider
            theme={{
              ...antdTheme,
              algorithm: isDarkTheme
                ? antTheme.darkAlgorithm
                : antTheme.defaultAlgorithm,
            }}
          >
            <GlobalStyles />
            {!isDOMLoaded ? (
              <img src="/assets/RTW-LOGO-BLUE.gif" className="animating-logo" />
            ) : (
              <>
                <HeaderComponent />
                <Component {...pageProps} />
                <FloatingContactButton
                  onClick={() =>
                    scrollToSectionWithOffset(
                      router,
                      "/contact-us",
                      "contact-us-form"
                    )
                  }
                >
                  <MailOutlined className="icon" size={32} />
                </FloatingContactButton>
              </>
            )}
          </ConfigProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </MainWrapper>
  )
}

export default MyApp
